import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Avatar, Button, Combobox, Dialog, Spinner, Switch } from "@/components/ui";
import { extractInitials } from "@/utils";
import { Svg } from "@/assets";
import { User, UsersServices, VideoProject, VideoProjectServices } from "@/services/bedrock";

interface OwnProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  coachId: number;
  coachName: string;
}

interface StaffOption {
  key: number;
  text: string;
  value: number;
  avatarUrl?: string;
}

function staffDisplayName(user: User): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  if (name.length > 0) return name;
  return user.email || `User ${user.id}`;
}

function assignmentIdsForRole(
  assignments: VideoProject.QueueAssignment[] | undefined,
  role: VideoProject.QueueAssignment["role"]
): number[] {
  return (assignments ?? []).filter(assignment => assignment.role === role).map(assignment => assignment.staffUserId);
}

function sortedIds(ids: number[]): number[] {
  return [...ids].sort((a, b) => a - b);
}

function sameIds(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const left = sortedIds(a);
  const right = sortedIds(b);
  return left.every((id, i) => id === right[i]);
}

export function VideoRoutingDialog({ open, onOpenChange, coachId, coachName }: OwnProps) {
  const queryClient = useQueryClient();
  const [requireQaOnVideos, setRequireQaOnVideos] = React.useState(false);
  const [editorIds, setEditorIds] = React.useState<number[]>([]);
  const [qaIds, setQaIds] = React.useState<number[]>([]);
  const [saving, setSaving] = React.useState(false);

  const {
    data: assignmentsPayload,
    isLoading: loadingAssignments,
    isError: assignmentsError,
  } = useQuery({
    queryKey: ["coachQueueAssignments", coachId],
    queryFn: () => VideoProjectServices.getCoachQueueAssignments(coachId),
    enabled: open && Number.isFinite(coachId),
  });

  const { data: editorsData, isLoading: loadingEditors } = useQuery({
    queryKey: ["editors"],
    queryFn: () => UsersServices.getUsers({ userType: User.Type.EDITOR }),
    enabled: open,
    staleTime: 1000 * 60 * 30,
  });

  React.useEffect(() => {
    if (!open || !assignmentsPayload) return;
    setRequireQaOnVideos(assignmentsPayload.requireQaOnVideos);
    setEditorIds(assignmentIdsForRole(assignmentsPayload.assignments, "EDITOR"));
    setQaIds(assignmentIdsForRole(assignmentsPayload.assignments, "QA"));
  }, [open, assignmentsPayload]);

  const staffOptions = React.useMemo<StaffOption[]>(() => {
    const byId = new Map<number, StaffOption>();

    assignmentsPayload?.assignments.forEach(assignment => {
      if (byId.has(assignment.staffUserId)) return;
      byId.set(assignment.staffUserId, {
        key: assignment.staffUserId,
        text: assignment.staffUser ? staffDisplayName(assignment.staffUser) : `User ${assignment.staffUserId}`,
        value: assignment.staffUserId,
        avatarUrl: assignment.staffUser?.photoUrl || undefined,
      });
    });

    (editorsData?.content || []).forEach(editor => {
      byId.set(editor.id, {
        key: editor.id,
        text: staffDisplayName(editor),
        value: editor.id,
        avatarUrl: editor.photoUrl || Svg.noImageuser,
      });
    });

    return [...byId.values()].sort((a, b) => a.text.localeCompare(b.text));
  }, [assignmentsPayload, editorsData]);

  const hasChanges =
    !!assignmentsPayload &&
    (requireQaOnVideos !== assignmentsPayload.requireQaOnVideos ||
      !sameIds(editorIds, assignmentIdsForRole(assignmentsPayload.assignments, "EDITOR")) ||
      !sameIds(qaIds, assignmentIdsForRole(assignmentsPayload.assignments, "QA")));

  const loading = loadingAssignments || (loadingEditors && staffOptions.length === 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      await VideoProjectServices.updateCoachQueueAssignments(coachId, {
        requireQaOnVideos,
        assignments: [
          ...editorIds.map(staffUserId => ({ staffUserId, role: "EDITOR" as const })),
          ...qaIds.map(staffUserId => ({ staffUserId, role: "QA" as const })),
        ],
      });
      await queryClient.invalidateQueries({ queryKey: ["coachQueueAssignments", coachId] });
      toast.success("Video routing updated");
      onOpenChange(false);
    } catch (error) {
      console.error("Error updating video routing", error);
      toast.error("Failed to update video routing");
    } finally {
      setSaving(false);
    }
  };

  const staffComboboxIcon = (item: StaffOption) => (
    <Avatar className="size-5 mr-1.5">
      <Avatar.Image src={item.avatarUrl} />
      <Avatar.Fallback className="text-[8px]">{extractInitials(item.text)}</Avatar.Fallback>
    </Avatar>
  );

  const staffRoleFields = [
    { label: "Editors", placeholder: "Select editors", ids: editorIds, setIds: setEditorIds },
    { label: "QA", placeholder: "Select QA reviewers", ids: qaIds, setIds: setQaIds },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg" showCloseButton>
      <Dialog.Header>
        <Dialog.Title>Video Routing</Dialog.Title>
        <Dialog.Description>{coachName}</Dialog.Description>
      </Dialog.Header>
      <Dialog.Content className="flex flex-col gap-5 py-4">
        {assignmentsError ? (
          <p className="text-sm text-destructive m-0">Unable to load video routing for this Coach.</p>
        ) : loading ? (
          <div className="flex items-center justify-center py-10">
            <Spinner className="size-6 text-muted-foreground" />
          </div>
        ) : (
          <>
            <p className="text-xs text-muted-foreground m-0 leading-relaxed">
              Assigned staff get this Coach in their Priority queue. Coaches with no assignments stay in the General
              queue.
            </p>
            <div className="flex items-center gap-2.5">
              <Switch
                id={`require-qa-on-videos-${coachId}`}
                checked={requireQaOnVideos}
                disabled={saving}
                onCheckedChange={setRequireQaOnVideos}
              />
              <label
                htmlFor={`require-qa-on-videos-${coachId}`}
                className="text-sm font-medium text-foreground select-none cursor-pointer leading-none"
              >
                Require QA on Videos
              </label>
            </div>
            {staffRoleFields.map(field => (
              <div key={field.label} className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-foreground uppercase tracking-wider">{field.label}</label>
                <Combobox
                  className="w-full bg-white"
                  placeholder={field.placeholder}
                  multiple
                  searchable
                  items={staffOptions}
                  value={staffOptions.filter(option => field.ids.includes(option.value))}
                  onValueChange={(selectedItems: StaffOption[]) => field.setIds(selectedItems.map(item => item.value))}
                  itemToStringLabel={item => item.text}
                  itemToIcon={staffComboboxIcon}
                  maxChips="auto"
                  disabled={saving}
                />
              </div>
            ))}
          </>
        )}
      </Dialog.Content>
      <Dialog.Footer>
        <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!hasChanges || saving || loading || assignmentsError}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}
